import { AnalystChartSpec, AnalystTableSpec } from '@digital-twins/shared-types'

import { Result } from '@/components/quick-study-results/quickStudyTypes'

import { FindingAnalystSpecs } from './findingAnalystSpecs'

/**
 * Dev-only numeric parity between client-row aggregation and a server spec.
 *
 * Cache scope: one-shot, not cached.
 *
 * Two sources of truth (client tallies vs FactBundle specs) require an
 * automated check while both paths exist. Production builds skip this.
 */

export const FINDING_PLOT_PARITY_EPSILON = 1

export interface FindingPlotParityMismatch {
  key: string
  client: number
  server: number
}

export interface FindingPlotParityResult {
  ok: boolean
  compared: number
  mismatches: Array<FindingPlotParityMismatch>
}

function normalizeParityKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function sharePct(count: number, total: number): number {
  if (total <= 0) {
    return 0
  }
  return (count * 100) / total
}

function clientShareMap(result: Result): Map<string, number> {
  const shares = new Map<string, number>()
  const questionName = result.question?.name
  if (!questionName) {
    return shares
  }

  const counts = new Map<string, number>()
  let total = 0

  for (const row of result.responses ?? []) {
    const raw = row.survey_data?.[questionName]
    const values = Array.isArray(raw) ? raw : raw == null ? [] : [raw]
    const keys: Array<string> = []
    for (const value of values) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        const key = normalizeParityKey(String(value))
        if (key && !keys.includes(key)) {
          keys.push(key)
        }
      }
    }
    if (keys.length === 0) {
      continue
    }
    total += 1
    for (const key of keys) {
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }

  counts.forEach((count, key) => {
    shares.set(key, sharePct(count, total))
  })
  return shares
}

// A share is the figure's typed format (a percent of 100), never its unit label.
function chartUsesShareParity(chart: AnalystChartSpec): boolean {
  return chart.series.length > 0 && chart.series.every((series) => series.valueFormat === 'percent')
}

function tableUsesShareParity(table: AnalystTableSpec): boolean {
  return table.columns.length === 1 && table.columns[0].valueFormat === 'percent'
}

export function isFindingPlotParityComparable(specs: FindingAnalystSpecs): boolean {
  if (specs.charts.length > 0) {
    return chartUsesShareParity(specs.charts[0])
  }
  if (specs.tables.length > 0) {
    return tableUsesShareParity(specs.tables[0])
  }
  return false
}

function chartShareMap(chart: AnalystChartSpec): Map<string, number> {
  const shares = new Map<string, number>()
  if (chart.series.length === 1) {
    const series = chart.series[0]
    for (const datum of series.data) {
      if (datum.isSuppressed || datum.value == null) {
        continue
      }
      shares.set(normalizeParityKey(datum.category), datum.value)
    }
    return shares
  }

  const firstCategory = chart.series[0]?.data[0]?.category
  const isSingleCategory = chart.series.every(
    (series) => series.data.length === 1 || series.data.every((datum) => datum.category === firstCategory),
  )
  if (isSingleCategory) {
    for (const series of chart.series) {
      const datum = series.data.find((entry) => entry.category === firstCategory) ?? series.data[0]
      if (!datum || datum.isSuppressed || datum.value == null) {
        continue
      }
      shares.set(
        normalizeParityKey(series.label || series.id),
        datum.value,
      )
    }
  }
  return shares
}

function tableShareMap(table: AnalystTableSpec): Map<string, number> {
  const shares = new Map<string, number>()
  if (table.columns.length !== 1) {
    return shares
  }
  const columnId = table.columns[0].id
  for (const row of table.rows) {
    const cell = row.cells.find((entry) => entry.columnId === columnId)
    if (!cell || cell.isSuppressed || cell.value == null) {
      continue
    }
    shares.set(normalizeParityKey(row.label), cell.value)
  }
  return shares
}

function serverShareMap(specs: FindingAnalystSpecs): Map<string, number> {
  if (specs.charts.length > 0) {
    return chartShareMap(specs.charts[0])
  }
  if (specs.tables.length > 0) {
    return tableShareMap(specs.tables[0])
  }
  return new Map()
}

export function compareFindingPlotParity(
  result: Result,
  specs: FindingAnalystSpecs,
): FindingPlotParityResult {
  if (!isFindingPlotParityComparable(specs)) {
    return {
      ok: true,
      compared: 0,
      mismatches: [],
    }
  }

  const client = clientShareMap(result)
  const server = serverShareMap(specs)
  const mismatches: Array<FindingPlotParityMismatch> = []
  let compared = 0

  server.forEach((serverValue, key) => {
    if (!client.has(key)) {
      return
    }
    compared += 1
    const clientValue = client.get(key) ?? 0
    if (Math.abs(clientValue - serverValue) > FINDING_PLOT_PARITY_EPSILON) {
      mismatches.push({ key, client: clientValue, server: serverValue })
    }
  })

  return {
    ok: mismatches.length === 0,
    compared,
    mismatches,
  }
}

export function isFindingPlotParityDev(): boolean {
  return Boolean(import.meta.env.DEV)
}

/**
 * When both the client-row path and a server spec exist, compare numbers.
 * Dev builds only: console.error + a result the renderer can flag.
 */
export function reportFindingPlotParity(
  result: Result,
  specs: FindingAnalystSpecs,
): FindingPlotParityResult | null {
  if (!isFindingPlotParityDev()) {
    return null
  }
  if ((result.responses?.length ?? 0) === 0) {
    return null
  }
  if (specs.charts.length === 0 && specs.tables.length === 0) {
    return null
  }

  const comparison = compareFindingPlotParity(result, specs)
  if (comparison.compared === 0) {
    return comparison
  }
  if (!comparison.ok) {
    console.error('PlotRenderer/findingPlotParity - mismatch', comparison)
  }
  return comparison
}
